// src/pages/IntakeSubmission.jsx
//
// One print submission, /intake/:reference. The same page serves its author and
// the team; what each sees is switched by what the server says they may do:
//
//   both   — the status, what was submitted (files and the frozen book's PDF;
//            for the author, their own), and the history
//   author — the team's notes and, once sent back, Resubmit
//   team   — Approve, or Send back with notes
//
// It promises an email only when the server says email is switched on.
//
// Nothing here edits a submission in place. A sent-back book is resubmitted as
// a new version under the same reservation number (backend/routes/intake.py).

import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import Field from '../components/common/Field';
import { tokens } from '../theme';
import IntakeShell from '../components/intake/IntakeShell';
import StatusChip from '../components/intake/StatusChip';
import DetailRows from '../components/intake/DetailRows';
import FileDrop from '../components/intake/FileDrop';
import SpecsForm from '../components/intake/SpecsForm';
import ProofPreview from '../components/intake/ProofPreview';
import useQuote from '../components/intake/useQuote';
import { intakeApi } from '../components/intake/api';
import { UPLOAD_RULES, fileProblem, formatBytes, isValidEmail, money, openingSpecs } from '../utils/intake';
import { inches } from '../utils/printSizes';
import {
  SP,
  actionsSx,
  noteBoxSx,
  panelSubSx,
  panelSx,
  panelTitleSx,
  primaryButtonSx,
} from '../components/intake/intakeSx';

const EVENT_LABEL = {
  submitted: 'Submitted',
  resubmitted: 'Resubmitted',
  sent_back: 'Sent back with notes',
  approved: 'Approved',
};

function formatDate(iso) {
  const when = iso ? new Date(iso) : null;
  if (!when || Number.isNaN(when.getTime())) return '';
  return when.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Notes({ children }) {
  return (
    <Box sx={noteBoxSx(tokens.color.warning, tokens.color.warningTint)} data-testid="intake-review-notes">
      <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{children}</Typography>
    </Box>
  );
}

function ErrorText({ children }) {
  return (
    <Typography role="alert" sx={{ color: tokens.color.error, fontSize: 13, mt: 2 }}>
      {children}
    </Typography>
  );
}

// The status, in the author's words or the team's.
function StatusPanel({ sub }) {
  const review = sub.review || {};
  if (sub.status === 'changes_requested') {
    return (
      <Box sx={panelSx}>
        <Typography component="h2" sx={panelTitleSx}>
          Changes requested
        </Typography>
        <Typography sx={panelSubSx}>
          {sub.is_owner
            ? 'The production team looked at your book and asked for some changes before it can be printed.'
            : 'Sent back to the author.'}{' '}
          {formatDate(review.at)}
        </Typography>
        {review.notes && <Notes>{review.notes}</Notes>}
      </Box>
    );
  }
  if (sub.status === 'approved') {
    return (
      <Box sx={panelSx}>
        <Typography component="h2" sx={panelTitleSx}>
          Approved
        </Typography>
        <Typography sx={panelSubSx}>
          {sub.is_owner
            ? "The production team approved your book. We'll be in touch about the next steps."
            : 'Approved for printing.'}{' '}
          {formatDate(review.at)}
        </Typography>
        {review.notes && <Notes>{review.notes}</Notes>}
      </Box>
    );
  }
  return (
    <Box sx={panelSx}>
      <Typography component="h2" sx={panelTitleSx}>
        Under review
      </Typography>
      <Typography sx={panelSubSx}>
        {sub.is_owner
          ? `ReVisionari's production team is reviewing your book. ${
              sub.email_enabled
                ? "We'll email you when it's approved, or if anything needs a change."
                : 'Check this page for their answer.'
            }`
          : 'Waiting for review.'}{' '}
        Version {sub.version}, submitted {formatDate(sub.submitted_at)}.
      </Typography>
    </Box>
  );
}

// Approve, or send back with notes. Approving asks once more on the page
// itself; the viewer cannot show confirm() dialogs reliably everywhere.
function ReviewPanel({ sub, onDone }) {
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const act = async (kind) => {
    if (kind === 'send_back' && !notes.trim()) {
      setError('Say what needs to change before sending it back.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data =
        kind === 'approve'
          ? await intakeApi.approve(sub.reservation_number, notes.trim())
          : await intakeApi.sendBack(sub.reservation_number, notes.trim());
      onDone(data.submission, { emailSent: data.email_sent });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <Box sx={panelSx} data-testid="intake-review-panel">
      <Typography component="h2" sx={panelTitleSx}>
        Review
      </Typography>
      <Typography sx={panelSubSx}>
        {sub.email_enabled
          ? 'Notes are emailed to the author.'
          : 'The author sees your notes on their submission page.'}{' '}
        They&rsquo;re required when you send the book back, and optional when you approve it.
      </Typography>
      <Box sx={{ mt: 2.5 }}>
        <Field
          label="Notes for the author"
          multiline
          minRows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          inputProps={{ maxLength: 4000, 'data-testid': 'intake-review-notes-input' }}
        />
      </Box>
      {error && <ErrorText>{error}</ErrorText>}
      {confirming ? (
        <Box sx={{ ...actionsSx, justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: 13, mr: 'auto' }}>
            Approve this submission?{sub.email_enabled ? ' The author is emailed.' : ''}
          </Typography>
          <Button onClick={() => setConfirming(false)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => act('approve')}
            disabled={busy}
            sx={primaryButtonSx}
            data-testid="intake-approve-confirm"
          >
            {busy ? 'Approving…' : 'Approve'}
          </Button>
        </Box>
      ) : (
        <Box sx={{ ...actionsSx, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => act('send_back')}
            disabled={busy}
            sx={{ textTransform: 'none' }}
            data-testid="intake-send-back"
          >
            {busy ? 'Sending…' : 'Send back with notes'}
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirming(true)}
            disabled={busy}
            sx={primaryButtonSx}
            data-testid="intake-approve"
          >
            Approve
          </Button>
        </Box>
      )}
    </Box>
  );
}

// The author's way back in after the team sent the book back.
function ResubmitPanel({ sub, onDone }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState({ manuscript: null, cover: null });
  const [fileErrors, setFileErrors] = useState({});
  const [editSpecs, setEditSpecs] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [email, setEmail] = useState(sub.contact_email || '');
  const [emailTouched, setEmailTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { quote, error: quoteError } = useQuote(editSpecs ? specs : null);

  // A refrozen ReVisionari book, or a new manuscript file, needs a fresh proof
  // tick; an upload resubmitted with the same manuscript keeps its old one.
  const [proofFor, setProofFor] = useState(null);
  const proofSize = editSpecs && specs ? specs.size : (sub.specs || {}).size;
  const proofFile = sub.source === 'upload' ? files.manuscript : null;
  const needsProof = sub.source === 'revisionari' || Boolean(proofFile);
  const proofKey = `${sub.source === 'revisionari' ? sub.draft_id : proofFile ? `${proofFile.name}:${proofFile.size}` : ''}:${proofSize}`;
  const proofOk = !needsProof || proofFor === proofKey;

  useEffect(() => {
    if (!editSpecs || pricing) return undefined;
    let cancelled = false;
    intakeApi
      .pricing()
      .then((p) => {
        if (cancelled) return;
        setPricing(p);
        setSpecs(openingSpecs(p, sub.specs));
      })
      .catch(() => {
        if (!cancelled) setError("We couldn't load the print options. Refresh the page to try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [editSpecs, pricing, sub.specs]);

  const current = Object.fromEntries((sub.files || []).map((f) => [f.kind, f.name]));
  const pick = (kind, file) => {
    const problem = fileProblem(file, kind);
    setFileErrors((prev) => ({ ...prev, [kind]: problem }));
    setFiles((prev) => ({ ...prev, [kind]: problem ? null : file }));
  };

  const resubmit = async () => {
    if (editSpecs && !isValidEmail(email)) {
      setEmailTouched(true);
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      ...(editSpecs && specs ? { contact_email: email.trim(), specs } : {}),
      ...(needsProof && proofFor === proofKey ? { proof_confirmed: true } : {}),
    };
    try {
      const data = await intakeApi.resubmit(sub.reservation_number, payload, sub.source === 'upload' ? files : null);
      onDone(data.submission, { resubmitted: true });
    } catch (err) {
      setError(err.status ? err.message : "We couldn't reach ReVisionari. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={panelSx} data-testid="intake-resubmit-panel">
      <Typography component="h2" sx={panelTitleSx}>
        Resubmit
      </Typography>
      {sub.source === 'revisionari' ? (
        <Typography sx={panelSubSx}>
          Make the changes in the editor, then resubmit here. We&rsquo;ll take a fresh copy of your book as it is
          when you press Resubmit.
        </Typography>
      ) : (
        <Typography sx={panelSubSx}>
          Upload a new manuscript or cover if they changed. A file you leave empty keeps the one you sent before.
        </Typography>
      )}

      {sub.source === 'revisionari' && sub.draft_id && (
        <Button
          variant="outlined"
          onClick={() => navigate(`/editor/${sub.draft_id}`)}
          sx={{ mt: 2, textTransform: 'none' }}
        >
          Open in editor
        </Button>
      )}

      {sub.source === 'upload' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75, mt: 2.5 }}>
          {['manuscript', 'cover'].map((kind) => (
            <FileDrop
              key={kind}
              kind={kind}
              label={kind === 'manuscript' ? 'New manuscript file' : 'New cover file'}
              hint={`${UPLOAD_RULES[kind].extensions.join(' ').replace(/\./g, '').toUpperCase()} · up to 50 MB`}
              accept={UPLOAD_RULES[kind].extensions.join(',')}
              file={files[kind]}
              error={fileErrors[kind]}
              onFile={(f) => pick(kind, f)}
              onRemove={() => setFiles((prev) => ({ ...prev, [kind]: null }))}
              keepNote={current[kind] ? `Keeps ${current[kind]}` : undefined}
            />
          ))}
        </Box>
      )}

      <Button onClick={() => setEditSpecs((v) => !v)} sx={{ mt: 2, px: 0, textTransform: 'none' }}>
        {editSpecs ? 'Keep the book specs and contact email as they were' : 'Change the book specs or contact email'}
      </Button>
      {editSpecs && pricing && specs && (
        <SpecsForm
          pricing={pricing}
          specs={specs}
          onChange={setSpecs}
          email={email}
          onEmailChange={setEmail}
          emailTouched={emailTouched}
          onEmailBlur={() => setEmailTouched(true)}
          quote={quote}
          quoteError={quoteError}
        />
      )}

      {needsProof && (
        <ProofPreview
          draftId={sub.source === 'revisionari' ? sub.draft_id : null}
          file={proofFile}
          size={proofSize}
          confirmed={proofFor === proofKey}
          onConfirm={(yes) => setProofFor(yes ? proofKey : null)}
        />
      )}

      <Box sx={noteBoxSx(tokens.color.warning, tokens.color.warningTint)}>
        <Typography sx={{ fontSize: 13 }}>
          After you resubmit, your book can&rsquo;t be changed unless the reviewer sends it back again.
        </Typography>
      </Box>
      {error && <ErrorText>{error}</ErrorText>}
      <Box sx={{ ...actionsSx, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={resubmit}
          disabled={busy || !proofOk}
          sx={primaryButtonSx}
          data-testid="intake-resubmit"
        >
          {busy ? 'Resubmitting…' : sub.source === 'revisionari' ? 'Resubmit with my latest changes' : 'Resubmit'}
        </Button>
      </Box>
    </Box>
  );
}

function DetailsPanel({ sub }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const downloadPdf = async () => {
    setPdfBusy(true);
    setPdfError(null);
    try {
      await intakeApi.downloadBookPdf(sub.reservation_number, `${sub.title || 'Book'} (${sub.reservation_number}).pdf`);
    } catch (err) {
      setPdfError(err.message);
    } finally {
      setPdfBusy(false);
    }
  };

  const rows = [
    { label: 'Reservation number', value: sub.reservation_number },
    ...(sub.author ? [{ label: 'Author', value: sub.author }] : []),
    { label: 'Source', value: sub.source === 'revisionari' ? 'A book in ReVisionari' : 'Uploaded files' },
    ...(sub.files || []).map((f) => ({
      label: f.kind === 'manuscript' ? 'Manuscript file' : 'Cover',
      value: (
        <>
          {f.url ? (
            <Link href={f.url} target="_blank" rel="noopener noreferrer">
              {f.name}
              {f.size != null ? ` (${formatBytes(f.size)})` : ''}
            </Link>
          ) : (
            f.name
          )}
          {f.pdf && !f.pdf.unreadable ? ` · ${f.pdf.pages} pages, ${inches(f.pdf.width_in, f.pdf.height_in)}` : ''}
        </>
      ),
    })),
    { label: 'Contact email', value: sub.contact_email },
    {
      label: 'Proof approved by the author',
      value: sub.proof_confirmed_at ? formatDate(sub.proof_confirmed_at) : 'Not recorded',
    },
    ...(sub.spec_summary || []).map((row) => ({ label: row.label, value: String(row.value) })),
    ...(sub.quote && sub.quote.total != null
      ? [{ label: 'Estimated print total', value: `${money(sub.quote.total)} for ${sub.quote.quantity} copies` }]
      : []),
  ];

  return (
    <Box sx={panelSx}>
      <Typography component="h2" sx={panelTitleSx}>
        What was submitted
      </Typography>
      <Typography sx={panelSubSx}>
        Version {sub.version}, submitted {formatDate(sub.submitted_at)}. File links work for an hour; reload the page
        for fresh ones.
      </Typography>
      {sub.checks && sub.checks.length > 0 && (
        <Box sx={noteBoxSx(tokens.color.warning, tokens.color.warningTint)} data-testid="intake-checks">
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Check before printing</Typography>
          <Box component="ul" sx={{ m: '6px 0 0 18px', p: 0, fontSize: 13, '& li': { mt: '4px' } }}>
            {sub.checks.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </Box>
        </Box>
      )}
      <DetailRows rows={rows} testId="intake-details" />
      {sub.has_book_pdf && (
        <Box sx={{ mt: 2.5 }}>
          <Button variant="outlined" onClick={downloadPdf} disabled={pdfBusy} sx={{ textTransform: 'none' }}>
            {pdfBusy ? 'Preparing the PDF…' : 'Download the submitted book (PDF)'}
          </Button>
          <Typography sx={{ fontSize: 12, color: SP.gray, mt: 1 }}>
            Printed from the copy frozen at submit, at the book size on this order.
          </Typography>
          {pdfError && <ErrorText>{pdfError}</ErrorText>}
        </Box>
      )}
    </Box>
  );
}

function HistoryPanel({ history }) {
  return (
    <Box sx={panelSx}>
      <Typography component="h2" sx={panelTitleSx}>
        History
      </Typography>
      <Box component="ol" sx={{ listStyle: 'none', p: 0, m: '16px 0 0' }}>
        {(history || []).map((h, i) => (
          <Box
            component="li"
            key={`${h.event}-${h.at}-${i}`}
            sx={{ py: '9px', borderBottom: `1px solid ${SP.lineSoft}`, fontSize: 13 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {EVENT_LABEL[h.event] || h.event}
              </Box>
              <Box component="span" sx={{ color: SP.gray }}>
                {formatDate(h.at)}
              </Box>
            </Box>
            {h.notes && (
              <Typography sx={{ fontSize: 12.5, color: SP.gray, mt: '4px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {h.notes}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function IntakeSubmission() {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await intakeApi.submission(reference);
      setSub(data.submission);
    } catch (err) {
      setError(
        err.status === 404
          ? "We couldn't find that submission. Check the reservation number."
          : err.status
            ? err.message
            : "We couldn't reach ReVisionari. Check your connection and try again."
      );
    }
  }, [reference]);

  useEffect(() => {
    load();
  }, [load]);

  // Says an email went only when one did: with email off, the author finds
  // the answer on this page instead.
  const updated = (next, { resubmitted = false, emailSent = false } = {}) => {
    setSub(next);
    if (resubmitted) {
      setFlash(
        next.email_enabled
          ? "Resubmitted. We'll email you when the production team has looked at it."
          : "Resubmitted. Check this page for the production team's answer."
      );
    } else if (next.status === 'approved') {
      setFlash(emailSent ? 'Approved. The author has been emailed.' : 'Approved. The author will see it on their submission page.');
    } else {
      setFlash(
        emailSent
          ? 'Sent back. The author has been emailed your notes.'
          : 'Sent back. The author will see your notes on their submission page.'
      );
    }
  };

  return (
    <IntakeShell
      eyebrow="§ Print submission"
      title={sub ? sub.title || 'Untitled' : reference}
      intro={sub ? `Reservation number ${sub.reservation_number}` : undefined}
      aside={
        sub && (
          <Box sx={{ mt: 1.5 }}>
            <StatusChip status={sub.status} />
          </Box>
        )
      }
      onBack={() => navigate(-1)}
    >
      {!sub && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Box sx={panelSx}>
          <ErrorText>{error}</ErrorText>
          <Box sx={{ ...actionsSx, justifyContent: 'flex-start' }}>
            <Button onClick={load} sx={{ textTransform: 'none' }}>
              Try again
            </Button>
            <Button component={RouterLink} to="/intake" sx={{ textTransform: 'none' }}>
              Submit a book
            </Button>
          </Box>
        </Box>
      )}
      {sub && (
        <>
          {flash && (
            <Box sx={noteBoxSx(tokens.color.success, tokens.color.successTint)} role="status">
              <Typography sx={{ fontSize: 13 }}>{flash}</Typography>
            </Box>
          )}
          <StatusPanel sub={sub} />
          {sub.can_resubmit && <ResubmitPanel sub={sub} onDone={updated} />}
          {sub.can_review && <ReviewPanel sub={sub} onDone={updated} />}
          <DetailsPanel sub={sub} />
          <HistoryPanel history={sub.history} />
          {sub.is_team && (
            <Box sx={{ mt: 3 }}>
              <Button component={RouterLink} to="/print-queue" sx={{ textTransform: 'none', minHeight: 44, px: 1 }}>
                &larr; Back to the print queue
              </Button>
            </Box>
          )}
        </>
      )}
    </IntakeShell>
  );
}
